import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { movies } from "@/mocks/movies";
import { articles } from "@/mocks/news";
import { searchArticles, searchMovies } from "@/lib/content";
import { usePageMeta } from "@/hooks/usePageMeta";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import EmptyState from "@/components/feature/EmptyState";

const tabs = [
  { key: "movies", label: "电影" },
  { key: "articles", label: "影评资讯" },
];

export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";
  const [keyword, setKeyword] = useState(query);
  const [tab, setTab] = useState("movies");
  const navigate = useNavigate();

  useEffect(() => {
    setKeyword(query);
    setTab("movies");
  }, [query]);

  const movieResults = useMemo(() => searchMovies(query), [query]);
  const articleResults = useMemo(() => searchArticles(query), [query]);

  usePageMeta(
    query ? `搜索「${query}」- 80s电影` : "站内搜索 - 80s电影",
    query
      ? `80s电影站内搜索「${query}」的结果，包含相关影片资料与影评资讯。搜索为前端模拟，数据来自站内演示内容。`
      : "在 80s电影中搜索八十年代经典影片与影评文章，支持按电影和影评资讯分类查看结果。",
    "站内搜索,80年代电影搜索,经典电影,电影影评,80s电影"
  );

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/search?q=${encodeURIComponent(value)}` : "/search");
  };

  const total = movieResults.length + articleResults.length;
  const visibleMovies = tab === "movies" ? movieResults : [];
  const visibleArticles = tab === "articles" ? articleResults : [];

  return (
    <div className="bg-background-50">
      <div className="border-b border-background-200 bg-background-100/60">
        <div className="mx-auto max-w-page px-4 py-9 md:px-6 md:py-12">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "站内搜索" }]} />
          <h1 className="mt-5 font-heading text-3xl font-semibold text-foreground-950 md:text-4xl">
            站内搜索
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
            支持检索影片片名、原名、导演、类型与影评文章标题、正文。搜索在浏览器本地完成，不会请求任何后端接口。
          </p>

          <form onSubmit={submit} className="mt-6 flex flex-col gap-2 sm:flex-row">
            <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-2.5">
              <i className="ri-search-line text-secondary-500"></i>
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="输入影片名、导演或关键词，例如「科幻」"
                aria-label="搜索关键词"
                className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-secondary-500 focus:outline-none"
              />
              {keyword && (
                <button
                  type="button"
                  aria-label="清空输入"
                  onClick={() => setKeyword("")}
                  className="text-secondary-500 hover:text-primary-700"
                >
                  <i className="ri-close-circle-line"></i>
                </button>
              )}
            </div>
            <button
              type="submit"
              className="whitespace-nowrap rounded-md bg-primary-700 px-6 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-800"
            >
              开始搜索
            </button>
          </form>
        </div>
      </div>

      <div className="mx-auto max-w-page px-4 py-9 md:px-6 md:py-12">
        {query ? (
          <>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-foreground-700">
                关键词回显：<span className="font-medium text-primary-700">「{query}」</span>
                <span className="ml-2 text-xs text-secondary-600">共找到 {total} 条结果</span>
              </p>
              <Link to="/search" className="text-xs text-primary-700 hover:text-primary-600">
                重新搜索
              </Link>
            </div>

            <div className="mt-5 flex items-center gap-1 rounded-full bg-background-100 p-1 sm:w-fit">
              {tabs.map((item) => {
                const count = item.key === "movies" ? movieResults.length : articleResults.length;
                return (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => setTab(item.key)}
                    className={`flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                      tab === item.key
                        ? "bg-primary-700 text-background-50"
                        : "text-foreground-700 hover:text-primary-700"
                    }`}
                  >
                    {item.label}
                    <span
                      className={`rounded-full px-1.5 py-0.5 text-[10px] ${
                        tab === item.key ? "bg-background-50/20" : "bg-background-200 text-secondary-700"
                      }`}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="mt-8">
              {tab === "movies" &&
                (visibleMovies.length > 0 ? (
                  <div className="grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 lg:grid-cols-4">
                    {visibleMovies.map((movie) => (
                      <MovieCard key={movie.id} movie={movie} />
                    ))}
                  </div>
                ) : (
                  <EmptyState
                    icon="ri-movie-line"
                    title="没有找到相关电影"
                    description={`与「${query}」匹配的影片暂时没有。可以换一个片名、导演或类型关键词试试。`}
                    action={
                      <button
                        type="button"
                        onClick={() => navigate("/category")}
                        className="rounded-md bg-primary-700 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-800"
                      >
                        前往电影分类浏览
                      </button>
                    }
                  />
                ))}

              {tab === "articles" &&
                (visibleArticles.length > 0 ? (
                  <div className="divide-y divide-background-200 border-y border-background-200">
                    {visibleArticles.map((article) => (
                      <Link
                        key={article.id}
                        to={`/news/${article.id}`}
                        className="group flex items-start gap-4 py-5"
                      >
                        <div className="h-20 w-28 shrink-0 overflow-hidden rounded-md bg-secondary-200 sm:h-24 sm:w-36">
                          <img
                            src={article.cover}
                            alt={`${article.title} 配图`}
                            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                          />
                        </div>
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2 text-[11px] text-secondary-600">
                            <span className="rounded-sm bg-primary-100 px-1.5 py-0.5 text-primary-700">
                              {article.type}
                            </span>
                            <span>{article.date}</span>
                            <span>·</span>
                            <span>{article.author}</span>
                          </div>
                          <p className="mt-1.5 font-heading text-base font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700 md:text-lg">
                            {article.title}
                          </p>
                          <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-foreground-600">
                            {article.excerpt}
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <EmptyState
                    icon="ri-article-line"
                    title="没有找到相关影评"
                    description={`与「${query}」匹配的文章暂时没有。可以尝试更短的关键词，或浏览全部影评资讯。`}
                    action={
                      <button
                        type="button"
                        onClick={() => navigate("/news")}
                        className="rounded-md bg-primary-700 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-800"
                      >
                        浏览全部影评资讯
                      </button>
                    }
                  />
                ))}
            </div>
          </>
        ) : (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <EmptyState
              icon="ri-search-eye-line"
              title="输入关键词开始检索"
              description="可以试试「科幻」「库布里克」「爱情」或具体片名。搜索范围覆盖全部影片资料与影评正文。"
            />
            <div className="rounded-lg border border-background-200 bg-background-100/50 p-5">
              <p className="font-label text-[11px] uppercase tracking-[0.2em] text-secondary-600">
                大家都在搜
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {["回到未来", "科幻", "爱情", "库布里克", "八十年代", "影评"].map((item) => (
                  <Link
                    key={item}
                    to={`/search?q=${encodeURIComponent(item)}`}
                    className="rounded-full border border-background-300 bg-background-50 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                  >
                    {item}
                  </Link>
                ))}
              </div>
              <div className="mt-6 grid grid-cols-2 gap-3">
                <div className="rounded-md bg-background-50 p-3">
                  <p className="font-heading text-xl text-primary-700">{movies.length}</p>
                  <p className="mt-1 text-[11px] text-secondary-600">可检索影片</p>
                </div>
                <div className="rounded-md bg-background-50 p-3">
                  <p className="font-heading text-xl text-primary-700">{articles.length}</p>
                  <p className="mt-1 text-[11px] text-secondary-600">可检索文章</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}